// src/components/admin/BiometricManagement.js
import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useEmployees } from '../../contexts/EmployeeContext';
import biometricService from '../../services/biometric.service';
import {
    Fingerprint, Plus, X, Loader2, Trash2, Edit3, Search,
    CheckCircle, AlertTriangle, Server, Users, History,
    Wifi, WifiOff, RefreshCw, Upload, Clock
} from 'lucide-react';

// ─── Main Component with Tabs ─────────────────────────────────────
const BiometricManagement = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [activeTab, setActiveTab] = useState('devices');

    const tabs = [
        { id: 'devices', label: 'Devices', icon: Server },
        { id: 'mappings', label: 'User Mapping', icon: Users },
        { id: 'logs', label: 'Punch Logs', icon: History },
    ];

    return (
        <div className="space-y-4">
            {/* Tabs */}
            <div className={`tour-biometric-tabs flex gap-1 p-1 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                {tabs.map(t => (
                    <button key={t.id} onClick={() => setActiveTab(t.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            activeTab === t.id
                                ? isDark ? 'bg-gray-700 text-white' : 'bg-white text-gray-900 shadow-sm'
                                : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                        }`}>
                        <t.icon className="w-4 h-4" />
                        {t.label}
                    </button>
                ))}
            </div>

            {activeTab === 'devices' && <DevicesTab isDark={isDark} />}
            {activeTab === 'mappings' && <MappingsTab isDark={isDark} />}
            {activeTab === 'logs' && <LogsTab isDark={isDark} />}
        </div>
    );
};

// ─── Devices Tab ──────────────────────────────────────────────────
const DevicesTab = ({ isDark }) => {
    const [devices, setDevices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);

    useEffect(() => { loadDevices(); }, []);

    const loadDevices = async () => {
        setLoading(true);
        try {
            const res = await biometricService.getDevices();
            if (res.success) setDevices(res.devices || []);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleSave = async (data) => {
        if (editing) {
            await biometricService.updateDevice(editing.id, data);
        } else {
            await biometricService.registerDevice(data);
        }
        setShowForm(false);
        setEditing(null);
        loadDevices();
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this device? All user mappings will be preserved.')) return;
        await biometricService.deleteDevice(id);
        loadDevices();
    };

    const getHeartbeatStatus = (lastHeartbeat) => {
        if (!lastHeartbeat) return { label: 'Never', color: 'gray' };
        const diff = (Date.now() - new Date(lastHeartbeat).getTime()) / 60000;
        if (diff < 5) return { label: 'Online', color: 'green' };
        if (diff < 30) return { label: `${Math.round(diff)}m ago`, color: 'yellow' };
        return { label: 'Offline', color: 'red' };
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Biometric Devices</h2>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Register and manage ZKTeco/eSSL biometric devices for attendance tracking.
                    </p>
                </div>
                <button onClick={() => { setEditing(null); setShowForm(true); }}
                    className="tour-biometric-add-device flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                    <Plus className="w-4 h-4" /> Add Device
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
            ) : devices.length === 0 ? (
                <div className={`rounded-xl p-12 text-center border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <Fingerprint className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>No biometric devices registered</h3>
                    <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Add your ZKTeco or eSSL device to start tracking attendance via biometrics.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {devices.map(d => {
                        const hb = getHeartbeatStatus(d.last_heartbeat);
                        return (
                            <div key={d.id} className={`rounded-xl border p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <Server className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                                        <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{d.device_name}</h3>
                                    </div>
                                    <div className="flex gap-1">
                                        <button onClick={() => { setEditing(d); setShowForm(true); }} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                                            <Edit3 className="w-3.5 h-3.5 text-gray-500" />
                                        </button>
                                        <button onClick={() => handleDelete(d.id)} className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20">
                                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                        </button>
                                    </div>
                                </div>
                                <p className={`text-sm font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>SN: {d.device_serial}</p>
                                {d.device_model && <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{d.device_model}</p>}
                                {d.location && <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{d.location}</p>}
                                <div className="flex items-center gap-2 mt-3 flex-wrap">
                                    <span className={`text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
                                        hb.color === 'green' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                        hb.color === 'yellow' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' :
                                        hb.color === 'red' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                        'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                                    }`}>
                                        {hb.color === 'green' ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
                                        {hb.label}
                                    </span>
                                    {d.device_type && (
                                        <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                            {d.device_type}
                                        </span>
                                    )}
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        d.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                                    }`}>
                                        {d.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {showForm && (
                <DeviceFormModal isDark={isDark} device={editing}
                    onClose={() => { setShowForm(false); setEditing(null); }}
                    onSave={handleSave} />
            )}
        </div>
    );
};

// ─── Device Form Modal ────────────────────────────────────────────
const PROTOCOLS = [
    { value: 'zkteco', label: 'ZKTeco / eSSL', desc: 'Push SDK — K40, F18, X990, iClock series' },
    { value: 'anviz', label: 'Anviz', desc: 'REST API — C2 Pro, FaceDeep, W2 series' },
    { value: 'hikvision', label: 'HikVision', desc: 'ISAPI — DS-K1T series, face terminals' },
    { value: 'biomax', label: 'BioMax / ADMS', desc: 'ADMS protocol — N-BM series' },
    { value: 'generic', label: 'Generic / Other', desc: 'Simple JSON format — works with any device or middleware' },
    { value: 'csv', label: 'Manual (CSV Import)', desc: 'No network — upload attendance data manually' },
];

const DeviceFormModal = ({ isDark, device, onClose, onSave }) => {
    const [form, setForm] = useState({
        device_serial: device?.device_serial || '',
        device_name: device?.device_name || '',
        device_model: device?.device_model || '',
        device_type: device?.device_type || 'fingerprint',
        protocol: device?.settings?.protocol || 'zkteco',
        location: device?.location || '',
        ip_address: device?.ip_address || '',
        is_active: device?.is_active !== false,
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const handleSave = async () => {
        setError('');
        if (!form.device_serial.trim() || !form.device_name.trim()) {
            setError('Serial number and device name are required.');
            return;
        }
        setSaving(true);
        try {
            const { protocol, ...deviceData } = form;
            deviceData.settings = JSON.stringify({ protocol });
            await onSave(deviceData);
        }
        catch (err) { setError(err.response?.data?.error || 'Failed to save device'); }
        finally { setSaving(false); }
    };

    const inputCls = `w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500/20`;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className={`w-full max-w-md rounded-2xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'} p-6 max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{device ? 'Edit Device' : 'Register Device'}</h3>
                    <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Serial Number *</label>
                        <input value={form.device_serial} onChange={e => setForm({...form, device_serial: e.target.value})}
                            className={`${inputCls} font-mono`} placeholder="e.g., BSRK203560123" disabled={!!device} />
                        {!device && <p className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Found on the back of your device or in device settings.</p>}
                    </div>
                    <div>
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Device Name *</label>
                        <input value={form.device_name} onChange={e => setForm({...form, device_name: e.target.value})}
                            className={inputCls} placeholder="e.g., Main Entrance Scanner" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Model</label>
                            <input value={form.device_model} onChange={e => setForm({...form, device_model: e.target.value})}
                                className={inputCls} placeholder="e.g., ZKTeco K40" />
                        </div>
                        <div>
                            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Type</label>
                            <select value={form.device_type} onChange={e => setForm({...form, device_type: e.target.value})}
                                className={inputCls}>
                                <option value="fingerprint">Fingerprint</option>
                                <option value="face">Face Recognition</option>
                                <option value="card">Card/RFID</option>
                                <option value="multi">Multi-mode</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Protocol / Device Brand</label>
                        <select value={form.protocol} onChange={e => setForm({...form, protocol: e.target.value})} className={inputCls}>
                            {PROTOCOLS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                        </select>
                        <p className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {PROTOCOLS.find(p => p.value === form.protocol)?.desc}
                        </p>
                    </div>
                    {form.protocol !== 'csv' && (
                        <div className={`p-3 rounded-lg border ${isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-blue-50 border-blue-200'}`}>
                            <p className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Push URL for this device:</p>
                            <code className={`text-xs font-mono break-all ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                {`${window.location.origin}/api/biometric/push/${form.protocol}`}
                            </code>
                            <p className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                Configure this URL in your device's push/cloud server settings.
                            </p>
                        </div>
                    )}
                    <div>
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Location</label>
                        <input value={form.location} onChange={e => setForm({...form, location: e.target.value})}
                            className={inputCls} placeholder="e.g., Office Main Entrance" />
                    </div>
                    <div>
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Device IP Address</label>
                        <input value={form.ip_address} onChange={e => setForm({...form, ip_address: e.target.value})}
                            className={`${inputCls} font-mono`} placeholder="e.g., 192.168.1.100" />
                    </div>
                    {device && (
                        <div>
                            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Status</label>
                            <button onClick={() => setForm({...form, is_active: !form.is_active})}
                                className={`w-full py-2 rounded-lg text-sm font-medium ${form.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                                {form.is_active ? 'Active' : 'Inactive'}
                            </button>
                        </div>
                    )}
                    {error && <p className="text-xs text-red-500">{error}</p>}
                </div>
                <div className="flex gap-3 mt-4 justify-end">
                    <button onClick={onClose} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}>Cancel</button>
                    <button onClick={handleSave} disabled={saving || !form.device_serial.trim() || !form.device_name.trim()}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── User Mappings Tab ────────────────────────────────────────────
const MappingsTab = ({ isDark }) => {
    const { employees } = useEmployees();
    const [mappings, setMappings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [search, setSearch] = useState('');

    useEffect(() => { loadMappings(); }, []);

    const loadMappings = async () => {
        setLoading(true);
        try {
            const res = await biometricService.getUserMappings();
            if (res.success) setMappings(res.mappings || []);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Remove this user mapping?')) return;
        await biometricService.deleteUserMapping(id);
        loadMappings();
    };

    const handleSave = async (data) => {
        await biometricService.createUserMapping(data);
        setShowForm(false);
        loadMappings();
    };

    const filtered = mappings.filter(m => {
        if (!search) return true;
        const s = search.toLowerCase();
        return (m.first_name + ' ' + m.last_name).toLowerCase().includes(s) ||
               m.employee_code?.toLowerCase().includes(s) ||
               m.biometric_user_id?.toString().includes(s);
    });

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>User Mapping</h2>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Map device user IDs to employees. Each employee gets a unique ID on the biometric device.
                    </p>
                </div>
                <button onClick={() => setShowForm(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                    <Plus className="w-4 h-4" /> Add Mapping
                </button>
            </div>

            {/* Search */}
            {mappings.length > 0 && (
                <div className="relative max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input value={search} onChange={e => setSearch(e.target.value)}
                        className={`w-full pl-9 pr-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                        placeholder="Search by name, code, or device ID..." />
                </div>
            )}

            {loading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
            ) : filtered.length === 0 ? (
                <div className={`rounded-xl p-12 text-center border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <Users className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{search ? 'No matching mappings' : 'No user mappings configured'}</h3>
                    <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {search ? 'Try a different search term.' : 'Map your employees to their biometric device user IDs to start tracking attendance.'}
                    </p>
                </div>
            ) : (
                <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                                    {['Employee', 'Code', 'Device User ID', 'Enrolled', 'Last Punch', ''].map(h => (
                                        <th key={h} className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(m => (
                                    <tr key={m.id} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                            {m.first_name} {m.last_name}
                                        </td>
                                        <td className={`px-4 py-3 text-sm font-mono ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{m.employee_code || '—'}</td>
                                        <td className={`px-4 py-3 text-sm font-mono font-medium ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{m.biometric_user_id}</td>
                                        <td className="px-4 py-3">
                                            <div className="flex gap-1">
                                                {m.fingerprint_count > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">FP:{m.fingerprint_count}</span>}
                                                {m.face_enrolled && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">Face</span>}
                                                {m.card_number && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">Card</span>}
                                                {!m.fingerprint_count && !m.face_enrolled && !m.card_number && <span className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>None</span>}
                                            </div>
                                        </td>
                                        <td className={`px-4 py-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            {m.last_punch_at ? new Date(m.last_punch_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                                        </td>
                                        <td className="px-4 py-3">
                                            <button onClick={() => handleDelete(m.id)} className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20">
                                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {showForm && (
                <MappingFormModal isDark={isDark} employees={employees} existingMappings={mappings}
                    onClose={() => setShowForm(false)} onSave={handleSave} />
            )}
        </div>
    );
};

// ─── Mapping Form Modal ───────────────────────────────────────────
const MappingFormModal = ({ isDark, employees, existingMappings, onClose, onSave }) => {
    const [form, setForm] = useState({ employee_id: '', biometric_user_id: '', fingerprint_count: 0, face_enrolled: false, card_number: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const mappedEmployeeIds = new Set(existingMappings.map(m => m.employee_id));
    const unmappedEmployees = employees.filter(e => !mappedEmployeeIds.has(e.id));

    const handleSave = async () => {
        setError('');
        if (!form.employee_id || !form.biometric_user_id) { setError('Employee and device user ID are required.'); return; }
        setSaving(true);
        try { await onSave(form); }
        catch (err) { setError(err.response?.data?.error || 'Failed to save mapping'); }
        finally { setSaving(false); }
    };

    const inputCls = `w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500/20`;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className={`w-full max-w-md rounded-2xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'} p-6`} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Add User Mapping</h3>
                    <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Employee *</label>
                        <select value={form.employee_id} onChange={e => setForm({...form, employee_id: e.target.value})} className={inputCls}>
                            <option value="">Select employee...</option>
                            {unmappedEmployees.map(e => (
                                <option key={e.id} value={e.id}>{e.first_name} {e.last_name} {e.employee_code ? `(${e.employee_code})` : ''}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Device User ID *</label>
                        <input value={form.biometric_user_id} onChange={e => setForm({...form, biometric_user_id: e.target.value})}
                            className={`${inputCls} font-mono`} placeholder="e.g., 101" />
                        <p className={`text-[10px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>The numeric user ID assigned to this employee on the biometric device.</p>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Fingerprints</label>
                            <input type="number" min="0" value={form.fingerprint_count} onChange={e => setForm({...form, fingerprint_count: parseInt(e.target.value) || 0})}
                                className={inputCls} />
                        </div>
                        <div>
                            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Face</label>
                            <button onClick={() => setForm({...form, face_enrolled: !form.face_enrolled})}
                                className={`w-full py-2 rounded-lg text-sm font-medium ${form.face_enrolled ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                                {form.face_enrolled ? 'Enrolled' : 'No'}
                            </button>
                        </div>
                        <div>
                            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Card #</label>
                            <input value={form.card_number} onChange={e => setForm({...form, card_number: e.target.value})}
                                className={inputCls} placeholder="Optional" />
                        </div>
                    </div>
                    {error && <p className="text-xs text-red-500">{error}</p>}
                </div>
                <div className="flex gap-3 mt-4 justify-end">
                    <button onClick={onClose} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}>Cancel</button>
                    <button onClick={handleSave} disabled={saving || !form.employee_id || !form.biometric_user_id}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Punch Logs Tab ───────────────────────────────────────────────
const LogsTab = ({ isDark }) => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reprocessing, setReprocessing] = useState(false);
    const [dateFilter, setDateFilter] = useState(new Date().toISOString().split('T')[0]);
    const [showImport, setShowImport] = useState(false);
    const [importData, setImportData] = useState('');
    const [importing, setImporting] = useState(false);

    useEffect(() => { loadLogs(); }, [dateFilter]);

    const loadLogs = async () => {
        setLoading(true);
        try {
            const res = await biometricService.getPunchLogs({ date: dateFilter, limit: 100 });
            if (res.success) setLogs(res.logs || []);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleReprocess = async () => {
        const failedIds = logs.filter(l => !l.processed).map(l => l.id);
        if (failedIds.length === 0) { alert('No unprocessed logs to reprocess.'); return; }
        setReprocessing(true);
        try {
            const res = await biometricService.reprocessLogs(failedIds);
            alert(`Reprocessed: ${res.processed} success, ${res.failed} failed out of ${res.total}`);
            loadLogs();
        } catch (err) { alert('Reprocess failed: ' + (err.response?.data?.error || err.message)); }
        finally { setReprocessing(false); }
    };

    const failedCount = logs.filter(l => !l.processed).length;

    const handleImport = async () => {
        setImporting(true);
        try {
            let punches;
            try { punches = JSON.parse(importData); } catch (_) {
                // Try CSV: split by newlines, first line = headers
                const lines = importData.trim().split('\n');
                if (lines.length < 2) { alert('Invalid format. Use JSON array or CSV.'); setImporting(false); return; }
                const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
                punches = lines.slice(1).map(line => {
                    const vals = line.split(',').map(v => v.trim());
                    const obj = {};
                    headers.forEach((h, i) => { obj[h] = vals[i]; });
                    return obj;
                });
            }
            if (!Array.isArray(punches)) punches = [punches];
            const res = await biometricService.importPunches(punches);
            alert(`Import complete: ${res.imported} imported, ${res.failed} failed out of ${res.total}${res.errors?.length ? '\n\nErrors:\n' + res.errors.join('\n') : ''}`);
            setShowImport(false);
            setImportData('');
            loadLogs();
        } catch (err) { alert('Import failed: ' + (err.response?.data?.error || err.message)); }
        finally { setImporting(false); }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Punch Logs</h2>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Raw biometric punch records from devices.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <input type="date" value={dateFilter} onChange={e => setDateFilter(e.target.value)}
                        className={`px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`} />
                    <button onClick={() => setShowImport(true)}
                        className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors">
                        <Upload className="w-4 h-4" /> Import
                    </button>
                    {failedCount > 0 && (
                        <button onClick={handleReprocess} disabled={reprocessing}
                            className="flex items-center gap-2 px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50">
                            {reprocessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                            Reprocess ({failedCount})
                        </button>
                    )}
                    <button onClick={loadLogs} className={`p-2 rounded-lg ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                        <RefreshCw className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
            ) : logs.length === 0 ? (
                <div className={`rounded-xl p-12 text-center border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <History className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>No punch logs for this date</h3>
                    <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Biometric punch data will appear here once devices are configured and employees start punching.
                    </p>
                </div>
            ) : (
                <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                                    {['Time', 'Employee', 'Device', 'Type', 'Method', 'Status'].map(h => (
                                        <th key={h} className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(l => (
                                    <tr key={l.id} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                                        <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                            {new Date(l.punch_time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                        </td>
                                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                            {l.first_name ? `${l.first_name} ${l.last_name}` : <span className={`italic ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Unmapped (ID: {l.biometric_user_id})</span>}
                                        </td>
                                        <td className={`px-4 py-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{l.device_name || l.device_serial}</td>
                                        <td className="px-4 py-3">
                                            <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${
                                                l.punch_type === 'Check In' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                            }`}>{l.punch_type || (l.att_state === 0 ? 'Check In' : 'Check Out')}</span>
                                        </td>
                                        <td className={`px-4 py-3 text-xs capitalize ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{l.verify_type_name || '—'}</td>
                                        <td className="px-4 py-3">
                                            {l.processed ? (
                                                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 inline-flex items-center gap-1">
                                                    <CheckCircle className="w-3 h-3" /> Processed
                                                </span>
                                            ) : (
                                                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 inline-flex items-center gap-1" title={l.error_message || ''}>
                                                    <AlertTriangle className="w-3 h-3" /> Failed
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Import Modal */}
            {showImport && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowImport(false)}>
                    <div className={`w-full max-w-lg rounded-2xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'} p-6`} onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Import Punch Data</h3>
                            <button onClick={() => setShowImport(false)}><X className="w-5 h-5 text-gray-400" /></button>
                        </div>
                        <p className={`text-xs mb-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Paste CSV or JSON data. CSV format: <code className="font-mono">device_serial,user_id,punch_time,type,method</code><br />
                            JSON format: <code className="font-mono">[{`{"serial":"SN","user_id":"101","time":"2026-04-09 09:00","type":"in","method":"fingerprint"}`}]</code>
                        </p>
                        <textarea value={importData} onChange={e => setImportData(e.target.value)}
                            rows={10}
                            className={`w-full px-3 py-2 rounded-lg border text-xs font-mono ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                            placeholder={`device_serial,user_id,punch_time,type,method\nTEST001,101,2026-04-09 09:00:00,in,fingerprint\nTEST001,101,2026-04-09 18:00:00,out,fingerprint`} />
                        <div className="flex gap-3 mt-4 justify-end">
                            <button onClick={() => setShowImport(false)} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}>Cancel</button>
                            <button onClick={handleImport} disabled={importing || !importData.trim()}
                                className="px-4 py-2 rounded-lg text-sm font-medium bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 transition-colors">
                                {importing ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Import'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BiometricManagement;
